import { ref, type Ref } from "vue";
import { useI18n } from "vue-i18n";
import { AppIdentifier } from "@/shared/@types/AppIdentifier";
import { useNotifications } from "@/shared/composables/useNotifications.composable";
import type { RegistrationFormData } from "@/app/registration-app/user/invited-registration/@types/RegistrationFormData";
import type { Message } from "@/app/registration-app/user/invited-registration/@types/Message";
import { registerInvitedUser } from "@/app/registration-app/user/invited-registration/services/registerInvitedUser.service";
import { registerFleetAtHomeUser } from "@/app/registration-app/user/invited-registration/services/registerFleetAtHomeUser.service";
import {
  RegisterFleetAtHomeUserMutationError,
  RegistrationSalutationInput,
  RegistrationType,
} from "@/shared/generated/graphql";
import { translateError } from "@/shared/helper/translateError.helper";

interface ParamsBase {
  salutation: RegistrationSalutationInput;
  firstName: string;
  lastName: string;
  newsletterConsent: boolean;
}

export const useRegisterUser = (
  provisioningKey: Ref<string>,
  registrationType: Ref<RegistrationType>,
  showMessage: (message: Message) => void,
) => {
  const { t } = useI18n();
  const { addSuccessSnackbar, addErrorSnackbar } = useNotifications();
  const isRegisteringUser = ref(false);

  const toLogin = () => {
    setTimeout(() => {
      window.location.href = `/${AppIdentifier.Equivolt}/stations`;
    }, 2000);
  };

  const showMessageDelayed = (message: Message) =>
    setTimeout(() => showMessage(message), 2000);

  const isInvitationLinkAlreadyUsedError = (error: string) => {
    return (
      error === RegisterFleetAtHomeUserMutationError.InvitationLinkAlreadyUsed
    );
  };

  const onInvitationLinkAlreadyUsed = () => {
    showMessage({
      title: t(
        "registration.user.invited.error.invitationLinkAlreadyUsed.title",
      ),
      text: t("registration.user.invited.error.invitationLinkAlreadyUsed.text"),
    });
  };

  const registerUserForLinkRegistration = async (
    registrationData: RegistrationFormData,
    paramsBase: ParamsBase,
  ) => {
    const { registrationFinished, caseNumber } = await registerInvitedUser({
      invitationId: provisioningKey.value,
      email: registrationData.email,
      ...paramsBase,
    });
    if (caseNumber) {
      showMessage({
        title: t("registration.user.failed.title"),
        text: t("registration.user.failed.info", { caseNumber }),
        subText: t("registration.user.failed.info2"),
      });
    } else {
      showMessageDelayed({
        title: registrationFinished
          ? t("registration.user.doubleOptIn.title")
          : t("registration.user.review.title"),
        text: registrationFinished
          ? t("registration.user.doubleOptIn.info")
          : t("registration.user.review.info"),
        subText: registrationFinished
          ? t("registration.user.doubleOptIn.info2")
          : t("registration.user.review.info2"),
      });
    }
  };

  const registerUserForEmailRegistration = async (
    registrationData: RegistrationFormData,
    paramsBase: ParamsBase,
  ) => {
    const result = await registerFleetAtHomeUser({
      provisioningKey: provisioningKey.value,
      password: registrationData.password,
      ...paramsBase,
    });
    if (result?.passwordSet) {
      toLogin();
    } else if (result && result?.errors && result.errors.length > 0) {
      const errorString = result.errors[0] as string;
      if (isInvitationLinkAlreadyUsedError(errorString)) {
        onInvitationLinkAlreadyUsed();
      } else {
        throw translateError(errorString);
      }
    } else {
      showMessage({
        title: t("registration.user.settingPasswordFailed.title"),
        text: t("registration.user.settingPasswordFailed.info"),
        subText: t("registration.user.settingPasswordFailed.info2"),
      });
    }
  };

  const onRegisteringUser = async (registrationData: RegistrationFormData) => {
    try {
      isRegisteringUser.value = true;
      const paramsBase = {
        salutation: registrationData.salutation,
        firstName: registrationData.firstName,
        lastName: registrationData.lastName,
        newsletterConsent: registrationData.newsletter,
      };
      if (registrationType.value === RegistrationType.Link) {
        await registerUserForLinkRegistration(registrationData, paramsBase);
      } else {
        await registerUserForEmailRegistration(registrationData, paramsBase);
      }
      addSuccessSnackbar(t("registration.shared.successNotification"), 2000);
    } catch (error) {
      addErrorSnackbar(error as string);
    } finally {
      isRegisteringUser.value = false;
    }
  };

  return {
    isRegisteringUser,
    onRegisteringUser,
  };
};
